import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { AlertController, NavParams } from "ionic-angular";
import moment from "moment";
import { IntercardDataService } from "../intercard-data/intercard-data.service";

/**
 * Generated class for the DynamicFormComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */
@Component({
  selector: "dynamic-form",
  templateUrl: "dynamic-form.html",
})
export class DynamicFormComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() assessmentId: string;
  @Input() sectionId: string;
  @Input() config;
  @Input() disabled: boolean = false;
  @Input() asmtTypeLookupCode: string;
  @Input() roleLookupCode: string;
  @Input() username: string;
  @Input() patientAdmittedDate: string;

  @Output() onValidityChange: EventEmitter<any> = new EventEmitter();
  @Output() onDataUpdate: EventEmitter<any> = new EventEmitter();
  @Output() onCardInitialized: EventEmitter<any> = new EventEmitter();

  formGroup: FormGroup;
  existingData: object;
  inputKeys: string[];
  emitters: object;
  id: string;
  name: string;
  access: string;
  depMap: object = {};
  depCounter: number = 0;
  layout: any;
  initialized: boolean = false;

  constructor(
    navParams: NavParams,
    public formBuilder: FormBuilder,
    public translatePipe: TranslateService,
    private alertCtrl: AlertController,
    public intercard: IntercardDataService
  ) {
    if (navParams.get("config")) {
      this.config = navParams.get("config");
    }
    if (navParams.get("roleLookupCode")) {
      this.roleLookupCode = navParams.get("roleLookupCode");
    }
    if (navParams.get("asmtTypeLookupCode")) {
      this.asmtTypeLookupCode = navParams.get("asmtTypeLookupCode");
    }
    if (navParams.get("username")) {
      this.username = navParams.get("username");
    }
  }

  ngOnInit(): void {
    if (
      this.config.hasOwnProperty("hisV2") &&
      this.config.hisV2 &&
      (this.config.id === "CL010.302" ||
        this.config.id === "CL010.212" ||
        this.config.id === "CL010.260" ||
        this.config.id === "CL010.213" ||
        this.config.id === "CL010.301")
    ) {
      this.config.patientAdmittedDate = this.patientAdmittedDate;
      for (let inputKey in this.config.inputs) {
        switch (inputKey) {
          case "hospitalizationDateAsked":
          case "spiritualExistentialDateAsked":
          case "treatmentDateAsked":
          case "dateAsked":
          case "dateComprehensivePainAssessment":
            this.config.inputs[inputKey].patientAdmittedDate =
              this.patientAdmittedDate;
        }
      }
    }

    this.id = this.config.id;
    this.name = this.config.cardName;
    this.access = this.config.access;
    if (this.config.emitters) {
      this.emitters = this.config.emitters;
    }

    this.inputKeys = Object.keys(this.config.inputs);
    for (let key of this.inputKeys) {
      this.config.inputs[key].id = key;
      // emptyCell type is used for layout purposes only, so remove from inputKeys to avoid creating a form control for it
      if (
        this.config.inputs[key].type === undefined ||
        this.config.inputs[key].type === "emptyCell" ||
        this.config.inputs[key].type === "translatedHeader"
      ) {
        let index = this.inputKeys.indexOf(key);
        this.inputKeys.splice(index, 1);
      }
    }

    if (this.access === "read" && this.config.inputs) {
      let inputs = this.config.inputs;
      for (let key of Object.keys(inputs)) {
        inputs[key].readonly = true;
      }
    }

    this.formGroup = this.formBuilder.group({});
    this.formGroup.valueChanges.subscribe((data) => {
      this.onValueChanges(data);
    });

    this.formGroup.statusChanges.subscribe((data) => {
      this.onStatusChanges();
    });

    for (let key of this.inputKeys) {
      this.formGroup.addControl(key, this.formBuilder.control(undefined));
    }

    if (this.existingData) {
      // this.formGroup.setValue( this.existingData );
      for (let key of Object.keys(this.existingData)) {
        if (this.formGroup.controls[key]) {
          this.formGroup.controls[key].setValue(this.existingData[key]);
        }
      }
    }

    this.layout = this.config.layout;
    if (this.config.hospiceAideCarePlanToggle) {
      this.layout = this.config.layoutToggle;
    }

    if (this.config.moreActions) {
      let deps: any;
      if (this.config.hospiceAideCarePlanToggle) {
        deps = this.config.dependenciesToggle;
      } else {
        deps = this.config.dependencies;
      }
      if (deps) {
        this.configurePostDataDependencies(deps);
      }
    }

    let emitters = this.emitters;
    if (emitters) {
      for (let key of Object.keys(emitters)) {
        let control = this.formGroup.controls[key];
        let dest = emitters[key]["destination"];
        if (control && dest) {
          control.valueChanges.subscribe((data) => {
            if (this.name === "height") {
              let height = control.value;
              sessionStorage.setItem("heightForBmi", JSON.stringify(height));
              this.intercard.emitData(dest, {
                calculateBmi: true,
                height: control.value,
              });
            } else {
              this.intercard.emitData(dest, { key: control.value });
            }
          });
        }
      }
    }
  }

  ngAfterViewInit() {
    let deps: any;
    let externalDeps: any;
    if (this.config.hospiceAideCarePlanToggle) {
      deps = this.config.dependenciesToggle;
    } else {
      deps = this.config.dependencies;
    }
    if (deps) {
      this.configurePreDataDependencies(deps);
    }

    if (deps) {
      this.configurePostDataDependencies(deps);
    }

    // this.onStatusChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled && this.formGroup) {
      this.disabled ? this.formGroup.disable() : this.formGroup.enable();
    }
  }

  moveToEnd(keys: Array<string>, string: string): Array<string> {
    let index = keys.indexOf(string);
    if (index > -1) {
      let spliced = keys.splice(index, 1);
      keys.push(spliced[0]);
    }

    return keys;
  }

  @Input() set data(incomingData: object) {
    if (incomingData) {
      this.existingData = incomingData;
      if (this.formGroup) {
        // this.formGroup.setValue( this.data );
        let keys = Object.keys(this.existingData);

        let sortedInputArray = [];
        for (let attr in this.config.inputs) {
          let index = this.config.inputs[attr]["sortOrder"] - 1;
          sortedInputArray[index] = attr;
        }

        for (let string of sortedInputArray) {
          keys = this.moveToEnd(keys, string);
        }

        for (let i = 0; i < keys.length; i++) {
          let key = keys[i];
          if (this.formGroup.controls[key]) {
            this.formGroup.controls[key].setValue(this.existingData[key]);
          }
        }
      }
    }

    if (incomingData !== undefined && !this.initialized) {
      this.initialized = true;
      this.onCardInitialized.emit(this.id);
    }
  }

  onValueChanges(data: any) {
    if (!this.disabled) {
      this.onValidityChange.emit(this.formGroup.valid);
      this.onDataUpdate.emit({ cardId: this.id, value: data });
    } else {
      this.onValidityChange.emit(true);
      this.onDataUpdate.emit({ cardId: this.id, value: {} });
    }
  }

  onStatusChanges() {
    if (!this.disabled) {
      this.onValidityChange.emit(this.formGroup.valid);
    } else {
      this.onValidityChange.emit(true);
    }
  }

  buildExternalSetDefaultValueDependency(
    dep: object,
    formControls: { [key: string]: AbstractControl }
  ) {
    this.intercard.register(dep["id"]).subscribe((x) => {
      if (formControls[dep["fields"][0]]) {
        // The field exists
        formControls[dep["fields"][0]].setValue(dep["values"][0]);
      }
    });
  }

  /**
   * Configure dependencies that are data driven and should be in place before loading state
   *
   * @param {Object[]} deps
   */
  configurePreDataDependencies(deps: object[]): void {
    let formControls = this.formGroup.controls;
    for (let dep of deps) {
      let id = dep["id"];
      switch (dep["type"]) {
        case "enable": {
          this.buildToggleDependency(true, dep, id, formControls);
          break;
        }

        case "disable": {
          this.buildToggleDependency(false, dep, id, formControls);
          break;
        }
        case "show": {
          this.buildShowDependency(dep, id, formControls);
          break;
        }
      }
    }
  }

  /**
   * Configure dependencies that are action driven and should not be in place before loading state
   *
   * @param {Object[]} deps
   */
  configurePostDataDependencies(deps: object[]): void {
    let formControls = this.formGroup.controls;
    for (let dep of deps) {
      let id = dep["id"];
      switch (dep["type"]) {
        case "clear": {
          this.buildClearDependency(dep, id, formControls);
          break;
        }

        case "copy": {
          this.buildCopyDependency(dep, id, formControls);
          break;
        }

        case "setOptions": {
          this.buildSetOptionsDependency(dep, id, formControls);
          break;
        }

        case "setOptionsWithId": {
          this.buildSetOptionsDependencyWithId(dep, id, formControls);
          break;
        }

        case "setValue": {
          this.buildSetValueDependency(dep, id, formControls);
          break;
        }

        case "setTrue": {
          this.buildSetTrueDependency(dep, id, formControls);
          break;
        }

        case "forceOther": {
          this.buildForceOtherDependency(dep, id, formControls);
          break;
        }

        case "sum": {
          this.buildSumDependency(dep, id, formControls);
          break;
        }

        case "show": {
          this.buildShowDependency(dep, id, formControls);
          break;
        }

        case "showControl": {
          this.buildShowControlDependency(dep, id, formControls);
          break;
        }

        case "addRules": {
          this.buildAddRulesDependency(dep, id, formControls);
          break;
        }

        case "removeRules": {
          this.buildRemoveRulesDependency(dep, id, formControls);
          break;
        }

        case "showErrorMessage": {
          this.buildShowErrorMessageDependency(dep, id, formControls);
          break;
        }

        case "addRequiredAttribute": {
          this.buildAddRequiredAttributeDependency(dep, id, formControls);
          break;
        }

        case "removeRequiredAttribute": {
          this.buildRemoveRequiredAttributeDependency(dep, id, formControls);
          break;
        }

        case "setTodayDate": {
          this.buildTodayDateDependency(dep, id, formControls);
          break;
        }

        case "externalSetDefaultValue": {
          this.buildExternalSetDefaultValueDependency(dep, formControls);
          break;
        }

        case "setDefaultValue": {
          this.buildSetDefaultValueDependency(dep, id, formControls);
          break;
        }
        case "exclusiveSet": {
          this.buildExclusiveSetDependency(dep, id, formControls);
          break;
        }
      }
    }
  }

  buildToggleDependency(
    enableOnMatch: boolean,
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    let depId = this.depCounter++;

    let toggleDep = (data) => {
      let enabled = this.checkDepEquivalence(data, dep["values"]);
      if (!enableOnMatch) {
        enabled = !enabled;
      }

      for (let field of dep["fields"]) {
        if (formControls[field] === undefined) {
          continue;
        }
        if (enableOnMatch) {
          let depType = "enable";
          if (enabled) {
            this.claimDepId(field, depType, depId);
          } else {
            this.unclaimDepId(field, depType, depId);
          }

          if (depControl.enabled) {
            if (this.getDepState(field, depType) == 0) {
              formControls[field].setValue(null);
              formControls[field].disable();
            } else {
              formControls[field].enable();
            }
          }
        } else {
          let depType = "disable";
          if (!enabled) {
            this.claimDepId(field, depType, depId);
          } else {
            this.unclaimDepId(field, depType, depId);
          }

          if (depControl.enabled) {
            if (this.getDepState(field, depType) == 0) {
              formControls[field].enable();
            } else {
              formControls[field].disable();
            }
          }
        }
      }
    };

    depControl.valueChanges.subscribe((data) => {
      toggleDep(data);
    });

    if (depControl.value !== undefined) {
      toggleDep(depControl.value);
    } else {
      toggleDep(null);
    }
  }

  buildForceOtherDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    depControl.valueChanges.subscribe((data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      for (let field of dep["fields"]) {
        if (formControls[field] === undefined) {
          continue;
        }
        let inputField = this.config.inputs[field];
        formControls[field].setValue(null);
        if (!matches) {
          while (inputField.options.length) {
            inputField.options.pop();
          }
          inputField.supportOtherOverride = true;
        }
        this.config.inputs[field].supportOtherOverride = !matches;
        this.formGroup.controls[field].updateValueAndValidity({
          onlySelf: true,
          emitEvent: true,
        });
      }
    });
  }

  buildSumDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let sum = 0; //absolute minimum sum is 4
    let counter = 0;
    let control = formControls[id];

    for (let field of dep["fields"]) {
      if (formControls[field] === undefined) {
        continue;
      }
      formControls[field].valueChanges.subscribe((data) => {
        sum = 0;
        counter = 0;
        for (let iterateField of dep["fields"]) {
          let fieldToSum = formControls[iterateField];
          if (fieldToSum.value !== null) {
            counter++;
          }
          sum += fieldToSum.value;
        }
        if (counter === dep["fields"].length) {
          control.setValue(sum);
        }
      });
    }

    //always override value to be sum
    control.valueChanges.subscribe((data) => {
      if (control.value !== sum && counter === dep["fields"].length) {
        control.setValue(sum);
      } else if (control.value !== null && counter < dep["fields"].length) {
        control.setValue(null);
      }
    });
  }

  buildClearDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }
    depControl.valueChanges.subscribe((data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      if (matches) {
        let clearValues = function () {
          for (let field of dep["fields"]) {
            if (formControls[field] === undefined) {
              continue;
            }
            formControls[field].setValue(null);
          }
        };

        let prompt = undefined;
        // let prompt = dep['prompt'];
        if (!prompt) {
          clearValues();
        } else {
          let alert = this.alertCtrl.create({
            title: this.translatePipe.instant(prompt),
            buttons: [
              {
                text: "Cancel",
              },
              {
                text: "Proceed",
                handler: () => {
                  clearValues();
                },
              },
            ],
          });
          alert.present();
        }
      }
    });
  }

  buildCopyDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }
    depControl.valueChanges.subscribe((data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      let sourceField = dep["sourceField"];
      if (matches && sourceField) {
        let valueToSet = formControls[sourceField].value;
        if (valueToSet != null && valueToSet != undefined) {
          let copyValue = function () {
            for (let field of dep["fields"]) {
              if (formControls[field] === undefined) {
                continue;
              }
              formControls[field].setValue(valueToSet);
            }
          };

          let prompt = dep["prompt"];
          if (!prompt) {
            copyValue();
          } else {
            let alert = this.alertCtrl.create({
              title: prompt,
              buttons: [
                {
                  text: "Cancel",
                },
                {
                  text: "Proceed",
                  handler: () => {
                    copyValue();
                  },
                },
              ],
            });
            alert.present();
          }
        }
      }
    });
  }

  buildShowDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      for (let field of dep["fields"]) {
        let inputField = this.config.inputs[field];
        if (!inputField) {
          console.warn(`Input field '${field}' not found in config.inputs.`);
          continue;
        }
        if (!inputField.rules) {
          inputField.rules = {};
        }
        inputField.rules.show = matches;
      }
    };

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  buildRemoveRulesDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      for (let field of dep["fields"]) {
        let inputField = this.config.inputs[field];
        if (matches) {
          delete inputField.rules;
        } else {
        }
      }
    };

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  buildAddRulesDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      for (let field of dep["fields"]) {
        let inputField = this.config.inputs[field];
        if (matches) {
          if (!inputField.rules) inputField["rules"] = inputField.otherRules;
        } else {
        }
      }
    };

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  buildAddRequiredAttributeDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      for (let field of dep["fields"]) {
        let inputField = this.config.inputs[field];
        if (matches) {
          if (inputField.rules && !inputField.rules.required) {
            inputField.rules.required = true;
          }
        } else {
        }
      }
    };

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  buildRemoveRequiredAttributeDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      let matches = false;
      if (dep["values"][0] === "dynamicValue" && data) {
        matches = true;
      } else {
        matches = this.checkDepEquivalence(data, dep["values"]);
      }
      for (let field of dep["fields"]) {
        let inputField = this.config.inputs[field];
        if (matches) {
          if (inputField.rules && inputField.rules.required) {
            inputField.rules.required = false;
          }
        } else {
        }
      }
    };

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  buildShowErrorMessageDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      for (let field of dep["fields"]) {
        let inputField = this.config.inputs[field];
        if (matches) {
          let value = false;
          let checkValues = dep["checkValues"];
          for (let value of checkValues) {
            value = this.formGroup.controls[value].value;
            if (value) {
              inputField.rules.show = false;
              break;
            } else {
              inputField.rules.show = true;
            }
          }
        } else {
          inputField.rules.show = false;
        }
      }
    };

    let checkValues = dep["checkValues"];
    for (let value of checkValues) {
      this.formGroup.controls[value].valueChanges.subscribe((data) => {
        data = depControl.value;
        if (data) {
          updateOptions(data);
        }
      });
    }

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  buildShowControlDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      for (let field of dep["fields"]) {
        let inputField = this.config.inputs[field];
        if (matches) {
          inputField.hidden = false;
        } else {
          inputField.hidden = true;
        }
      }
    };

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  buildSetOptionsDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);

      if (matches) {
        for (let field of dep["fields"]) {
          let inputField = this.config.inputs[field];
          // clear array
          while (inputField.options.length) {
            inputField.options.pop();
          }
          // set to new options
          for (let i in inputField.optionGroups[data]) {
            inputField.options.push(inputField.optionGroups[data][i]);
          }
        }
      }
    };

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  buildSetOptionsDependencyWithId(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);

      if (matches) {
        for (let field of dep["fields"]) {
          let inputField = this.config.inputs[field];
          // clear array
          while (inputField.options.length) {
            inputField.options.pop();
          }
          // set to new options
          for (let i in inputField.optionGroupsWithIds[data]) {
            inputField.options.push(inputField.optionGroupsWithIds[data][i]);
          }

          // set only selected option
          if (inputField.optionGroupsWithDetails) {
            var selOption = [];
            for (let i in inputField.optionGroupsWithDetails[data]) {
              selOption.push(inputField.optionGroupsWithDetails[data][i]);
            }
            inputField.selectedOptions = selOption;
          }
        }
      } else {
        for (let field of dep["fields"]) {
          let inputField = this.config.inputs[field];
          while (inputField.options.length) {
            inputField.options.pop();
          }

          for (let i in inputField.optionGroupsWithIds[data]) {
            inputField.options.push(inputField.optionGroupsWithIds[data][i]);
          }

          if (inputField.options.length == 0) {
            let obj = { display: "other", value: 0 };
            inputField.options.push(obj);
          }
        }
      }
    };

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  buildSetValueDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }

    let updateOptions = (data) => {
      if (data) {
        for (let field of dep["fields"]) {
          let inputField = this.config.inputs[field];

          if (this.config.inputs[id].selectedOptions) {
            var newArray = this.config.inputs[id].selectedOptions.filter(
              function (el) {
                return el.contactId == data;
              }
            );

            for (let i in newArray) {
              let optionArray = newArray[i];
              if (optionArray[field]) {
                formControls[field].setValue(optionArray[field]);
              }
            }
          }
        }
      } else if (data == 0) {
        //Set hidden id as zero
        formControls[id + "Id"].setValue(data);
      }
    };

    depControl.valueChanges.subscribe((data) => {
      updateOptions(data);
    });
    if (depControl.value !== undefined) {
      updateOptions(depControl.value);
    } else {
      updateOptions(null);
    }
  }

  checkDepEquivalence(data: any, values: any[]): boolean {
    if (typeof data == "object") {
      if (data instanceof Array) {
        for (let val of data) {
          if (values.indexOf(val) !== -1) {
            return true;
          }
        }

        if (data.length === 0 && values.length === 0) {
          return true;
        }
      } else if (data === null) {
        for (let val of values) {
          if (val == null) {
            return true;
          }
        }
      }
    } else {
      return values.indexOf(data) !== -1;
    }

    return false;
  }

  getDepState(field: string, depType: string) {
    let map = this.depMap;
    if (!map[field] || !map[field][depType]) {
      return 0;
    }
    return map[field][depType].size;
  }

  claimDepId(field: string, depType: string, id: number) {
    let map = this.depMap;
    if (!map[field]) {
      map[field] = {};
    }
    if (!map[field][depType]) {
      map[field][depType] = new Set();
    }
    map[field][depType].add(id);
  }

  unclaimDepId(field: string, depType: string, id: number) {
    let map = this.depMap;
    if (!map[field] || !map[field][depType]) {
      return;
    }
    map[field][depType].delete(id);
  }

  buildTodayDateDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }
    depControl.valueChanges.subscribe((data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      if (matches) {
        for (let field of dep["fields"]) {
          let value = formControls[field].value;
          if (!value) {
            let todayDate = moment().format("YYYYMMDD");
            formControls[field].setValue({ date: todayDate });
          }
        }
      }
    });
  }

  buildSetDefaultValueDependency(
    dep: object,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    let depControl = formControls[id];
    if (depControl === undefined) {
      return;
    }
    depControl.valueChanges.subscribe((data) => {
      let matches = this.checkDepEquivalence(data, dep["values"]);
      if (matches) {
        for (let field of dep["fields"]) {
          let inputField = this.config.inputs[field];
          let defaultValue = inputField?.default;
          formControls[field].setValue(defaultValue);
        }
      }
    });
  }

  /*
   * For "setTrue" dependencies:
   * - fields[0] = target field to update (checkbox array)
   * - fields[1] = specific value to add/remove in the target
   *
   * If the source field (dep.id) matches any value in `dep.values`,
   * fields[1] is added to fields[0]; otherwise, it is removed.
   */
  buildSetTrueDependency(
    dep: any,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    const depControl = formControls[id];
    const [targetField, valueToSet] = dep["fields"] || [];
    const valuesToWatch = dep["values"] || [];

    const update = (data: any) => {
      const targetControl = formControls[targetField];

      const current = Array.isArray(targetControl.value)
        ? [...targetControl.value]
        : [];

      let updated = [...current];

      const shouldAdd = Array.isArray(data)
        ? data.some((v) => valuesToWatch.includes(v))
        : valuesToWatch.includes(data);

      const alreadySet = updated.includes(valueToSet);

      if (shouldAdd && !alreadySet) {
        updated.push(valueToSet);
      } else if (!shouldAdd && alreadySet) {
        updated = updated.filter((v) => v !== valueToSet);
      }

      updated = Array.from(new Set(updated));

      targetControl.setValue(updated);
    };

    depControl.valueChanges.subscribe(update);
    update(depControl.value);
  }

  buildExclusiveSetDependency(
    dep: any,
    id: string,
    formControls: { [key: string]: AbstractControl }
  ) {
    const depControl = formControls[id];
    const exclusiveValues: (string | number)[] = dep.values || [];
    if (!depControl) return;

    let prevValues: any[] = Array.isArray(depControl.value)
      ? [...depControl.value]
      : [];
    let isPatching = false;

    const arraysEqual = (a: any[], b: any[]) =>
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((v, i) => v === b[i]);

    const isExclusive = (val: any) => {
      // direct match (id or label)
      if (
        exclusiveValues.includes(val) ||
        exclusiveValues.includes(String(val))
      )
        return true;

      // try to map via options on this input
      const inputField = this.config?.inputs?.[id];
      const opts: any[] = inputField?.options || [];
      const opt = opts.find(
        (o) => o?.value === val || String(o?.value) === String(val)
      );
      const label = opt?.display ?? opt?.label ?? opt?.description;
      return label ? exclusiveValues.includes(label) : false;
    };

    const update = (currentValues: any[]) => {
      if (isPatching) return;
      if (!Array.isArray(currentValues)) return;

      const exclusiveOnly = currentValues.filter(isExclusive);
      const regularOnly = currentValues.filter((v) => !isExclusive(v));

      // CASE 1: multiple exclusives -> keep the last one clicked
      if (exclusiveOnly.length > 1) {
        const newlyAdded = exclusiveOnly.find((v) => !prevValues.includes(v));
        const lastExclusive =
          newlyAdded || exclusiveOnly[exclusiveOnly.length - 1];
        const next = [lastExclusive];

        if (!arraysEqual(currentValues, next)) {
          isPatching = true;
          depControl.setValue(next);
          isPatching = false;
        }
      }

      // CASE 2: one exclusive + some regulars -> keep whichever was clicked LAST
      else if (exclusiveOnly.length === 1 && regularOnly.length > 0) {
        const added = currentValues.filter((v) => !prevValues.includes(v));
        const lastAdded = added[added.length - 1];
        const lastIsExclusive = isExclusive(lastAdded);
        const next = lastIsExclusive ? exclusiveOnly : regularOnly;

        if (!arraysEqual(currentValues, next)) {
          isPatching = true;
          depControl.setValue(next);
          isPatching = false;
        }
      }

      prevValues = Array.isArray(depControl.value)
        ? [...depControl.value]
        : [...currentValues];
    };

    depControl.valueChanges.subscribe(update);
    update(depControl.value);
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/dynamic-form/dynamic-form.ts